import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { COLORS, GRADIENT_UTILS } from '../lib/styleGuide';
import { 
  Search, 
  MoreCircle, 
  Document, 
  Call, 
  Show,
  ArrowLeft,
  User,
  Home,
  ChevronRight,
  Message
} from 'react-iconly';
import { BottomNavigation } from '../components/BottomNavigation';
import { BackButton } from '../components/ui';
import { 
  getOrderMessagesByOrderId, 
  sendOrderMessage, 
  subscribeToOrderMessagesByOrderId,
  getCustomerMessages,
  getUniqueOrdersFromCustomerMessages
} from '../lib/messagesService';
import { formatTimestamp } from '../lib/dateUtils';

export function CustomerMessages() {
  const { currentUser, userRole, hasRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const messagesEndRef = useRef(null);
  
  // State for chat management
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [allMessages, setAllMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  
  // Mobile responsive state
  const [isMobile, setIsMobile] = useState(false);
  const [mobileView, setMobileView] = useState('chats'); // 'chats', 'chat', 'order'
  const [selectedChatId, setSelectedChatId] = useState(null);

  // Get selected chat data
  const selectedChatData = chats.find(chat => chat.orderId === selectedChat);

  // Check if user is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Load all messages for the current user (customer)
  useEffect(() => {
    if (!currentUser?.uid) return;

    const loadMessages = async () => {
      setLoading(true);
      try {
        console.log('🔍 CustomerMessages - Loading messages for customer:', currentUser.uid);
        
        // Get all messages where current user is sender or receiver
        const userMessages = await getCustomerMessages(currentUser.uid);
        console.log('📨 CustomerMessages - Raw messages found:', userMessages);
        setAllMessages(userMessages);
        
        // Get unique orders from messages
        const uniqueOrders = await getUniqueOrdersFromCustomerMessages(userMessages, currentUser.uid);
        console.log('💬 CustomerMessages - Unique orders processed:', uniqueOrders);
        setChats(uniqueOrders);
        
        // Auto-select order if passed from navigation state
        if (location.state?.selectedOrderId && !selectedChat) {
          console.log('🔍 Customer looking for order with merchantOrderId:', location.state.selectedOrderId);
          console.log('📋 Available orders:', uniqueOrders.map(o => o.orderId));
          
          // Try to find exact match first (this should work with merchantOrderId)
          let targetOrder = uniqueOrders.find(order => order.orderId === location.state.selectedOrderId);
          
          if (targetOrder) {
            console.log('✅ Found exact match with merchantOrderId:', targetOrder.orderId);
            setSelectedChat(targetOrder.orderId);
            if (isMobile) {
              setSelectedChatId(targetOrder.orderId);
              setMobileView('chat');
            }
          } else {
            // Check if this is a test order before creating conversation
            const orderId = location.state.selectedOrderId;
            const isTestOrder = orderId && (
              orderId.includes('test') || 
              orderId.includes('TEST') ||
              orderId === 'ROSRL6' ||
              orderId.startsWith('test-') ||
              orderId.includes('WB9emD9PUdafcQ3CuBR6yiXF1l1') // Test customer ID
            );
            
            if (isTestOrder) {
              console.log('🚫 Skipping test order conversation creation:', orderId);
              return;
            }
            
            console.log('❌ No exact match found. Creating new conversation for merchantOrderId:', location.state.selectedOrderId);
            // Create a new conversation for this merchant order
            const newConversation = {
              orderId: location.state.selectedOrderId,
              sellerName: location.state.sellerName || 'Seller',
              sellerId: location.state.sellerId,
              lastMessage: 'Start a conversation...',
              lastTimestamp: new Date(),
              unread: false
            };
            
            // Add new conversation to the beginning of the list
            setChats([newConversation, ...uniqueOrders]);
            setSelectedChat(newConversation.orderId);
            if (isMobile) {
              setSelectedChatId(newConversation.orderId);
              setMobileView('chat');
            }
          }
        } else if (isMobile && uniqueOrders.length > 0 && !selectedChat) {
          // Auto-select first chat on mobile if no specific order requested
          setSelectedChat(uniqueOrders[0].orderId);
          setSelectedChatId(uniqueOrders[0].orderId);
        }
      } catch (error) {
        console.error('Error loading messages:', error);
      } finally {
        setLoading(false);
      }
    };

    loadMessages();
  }, [currentUser?.uid, isMobile]);

  // Subscribe to real-time messages for selected chat
  useEffect(() => {
    if (!selectedChat || !currentUser?.uid) return;

    const unsubscribe = subscribeToOrderMessagesByOrderId(selectedChat, (messages) => {
      setMessages(messages);
      
      // Scroll to bottom
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    });

    return unsubscribe;
  }, [selectedChat, currentUser?.uid]);

  // Send message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !currentUser?.uid || !selectedChatData) return;

    setSending(true);
    try {
      const messageData = {
        orderId: selectedChat,
        senderId: currentUser.uid,
        receiverId: selectedChatData.sellerId,
        senderName: currentUser.displayName || 'Customer',
        senderType: 'customer',
        message: newMessage.trim()
      };

      await sendOrderMessage(messageData);
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSending(false);
    }
  };

  // Mobile navigation functions
  const goToChats = () => {
    setMobileView('chats');
    setSelectedChatId(null);
  };

  const goToOrder = () => {
    setMobileView('order');
  };

  const goToChat = () => {
    setMobileView('chat');
  };



  const filteredChats = chats.filter(chat =>
    chat.orderId.toLowerCase().includes(searchTerm.toLowerCase()) ||
    chat.sellerName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Page Header */}
      <div style={{
        padding: '16px',
        borderBottom: '1px solid #E5E7EB',
        background: 'white',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginTop: '72px'
      }}>
        {isMobile && mobileView !== 'chats' ? (
          <BackButton
            onClick={mobileView === 'chat' ? goToChats : goToChat}
            variant="standard"
            ariaLabel="Back"
          />
        ) : (
          <BackButton
            onClick={() => navigate('/customer-dashboard')}
            variant="standard"
            ariaLabel="Back to Customer Dashboard"
          />
        )}
        <h1 style={{
          fontSize: '20px',
          fontWeight: '600',
          color: COLORS.NEUTRAL_TEXT,
          margin: 0
        }}>
          Messages
        </h1>
        {isMobile && mobileView === 'chat' && (
          <button
            onClick={goToOrder}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              borderRadius: '8px',
              marginLeft: 'auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Document size={20} color={COLORS.NEUTRAL_TEXT} />
          </button>
        )}
      </div>

      {/* Main Content */}
      <div style={{ 
        flex: 1, 
        display: 'flex',
        overflow: 'hidden'
      }}>
        {/* Chat List - Desktop or Mobile Chats View */}
        {(isMobile && mobileView === 'chats') || !isMobile ? (
          <div style={{
            width: isMobile ? '100%' : '350px',
            borderRight: isMobile ? 'none' : '1px solid #E5E7EB',
            background: 'white',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Search Bar */}
            <div style={{
              padding: '16px',
              borderBottom: '1px solid #E5E7EB'
            }}>
              <div style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center'
              }}>
                <Search size={20} color={COLORS.ACCENT_GRAY} style={{ position: 'absolute', left: '12px' }} />
                <input
                  type="text"
                  placeholder="Search orders..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 12px 12px 44px',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    fontSize: '14px',
                    outline: 'none'
                  }}
                />
              </div>
            </div>

            {/* Chat List */}
            <div style={{
              flex: 1,
              overflowY: 'auto',
              padding: '8px 0'
            }}>
              {loading ? (
                <div style={{
                  padding: '20px',
                  textAlign: 'center',
                  color: COLORS.ACCENT_GRAY
                }}>
                  Loading messages...
                </div>
              ) : filteredChats.length === 0 ? (
                <div style={{
                  padding: '20px',
                  textAlign: 'center',
                  color: COLORS.ACCENT_GRAY
                }}>
                  No messages yet
                </div>
              ) : (
                filteredChats.map((chat) => (
                  <div
                    key={chat.orderId}
                    onClick={() => {
                      setSelectedChat(chat.orderId);
                      if (isMobile) {
                        setSelectedChatId(chat.orderId);
                        setMobileView('chat');
                      }
                    }}
                    style={{
                      padding: '16px',
                      borderBottom: '1px solid #F3F4F6',
                      cursor: 'pointer',
                      background: selectedChat === chat.orderId ? '#F9FAFB' : 'white',
                      transition: 'background-color 0.2s'
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: '4px'
                    }}>
                      <span style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: COLORS.NEUTRAL_TEXT
                      }}>
                        Order #{chat.orderId.slice(-8).toUpperCase()}
                      </span>
                      <span style={{
                        fontSize: '12px',
                        color: COLORS.ACCENT_GRAY
                      }}>
                        {formatTimestamp(chat.lastMessage?.timestamp)}
                      </span>
                    </div>
                    <div style={{
                      fontSize: '14px',
                      color: COLORS.NEUTRAL_TEXT,
                      marginBottom: '4px'
                    }}>
                      {chat.sellerName}
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: COLORS.ACCENT_GRAY,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {chat.lastMessage || 'No messages yet'}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        ) : null}

        {/* Chat View - Desktop or Mobile Chat View */}
        {(isMobile && mobileView === 'chat') || !isMobile ? (
          selectedChat ? (
            <div style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              background: 'white'
            }}>
              {/* Chat Header */}
              <div style={{
                padding: '16px',
                borderBottom: '1px solid #E5E7EB',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <div>
                  <h3 style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: COLORS.NEUTRAL_TEXT,
                    margin: 0
                  }}>
                    Order #{selectedChat.slice(-8).toUpperCase()}
                  </h3>
                  <p style={{
                    fontSize: '14px',
                    color: COLORS.ACCENT_GRAY,
                    margin: '4px 0 0 0'
                  }}>
                    {selectedChatData?.sellerName}
                  </p>
                </div>
                <button
                  onClick={() => navigate(`/order-details/${selectedChat}`)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '8px',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Document size={20} color={COLORS.NEUTRAL_TEXT} />
                </button>
              </div>

              {/* Messages */}
              <div style={{
                flex: 1,
                overflowY: 'auto',
                padding: '16px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}>
                {messages.map((message) => (
                  <div
                    key={message.id}
                    style={{
                      alignSelf: message.senderId === currentUser?.uid ? 'flex-end' : 'flex-start',
                      maxWidth: '70%'
                    }}
                  >
                    <div style={{
                      background: message.senderId === currentUser?.uid ? COLORS.PRIMARY_GRADIENT : '#F3F4F6',
                      color: message.senderId === currentUser?.uid ? 'white' : COLORS.NEUTRAL_TEXT,
                      padding: '12px 16px',
                      borderRadius: '18px',
                      fontSize: '14px',
                      wordWrap: 'break-word'
                    }}>
                      {message.message}
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: COLORS.ACCENT_GRAY,
                      marginTop: '4px',
                      textAlign: message.senderId === currentUser?.uid ? 'right' : 'left'
                    }}>
                      {formatTimestamp(message.timestamp)}
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Message Input */}
              <div style={{
                padding: '16px',
                paddingBottom: '80px', // Add extra bottom padding for bottom navigation
                borderTop: '1px solid #E5E7EB',
                background: 'white'
              }}>
                <form onSubmit={handleSendMessage} style={{
                  display: 'flex',
                  gap: '12px',
                  alignItems: 'center'
                }}>
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type a message..."
                    style={{
                      flex: 1,
                      padding: '12px 16px',
                      border: '1px solid #E5E7EB',
                      borderRadius: '24px',
                      fontSize: '14px',
                      outline: 'none'
                    }}
                  />
                  <button
                    type="submit"
                    disabled={sending || !newMessage.trim()}
                    style={{
                      background: COLORS.PRIMARY_GRADIENT,
                      color: 'white',
                      border: 'none',
                      borderRadius: '50%',
                      width: '40px',
                      height: '40px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: sending || !newMessage.trim() ? 'not-allowed' : 'pointer',
                      opacity: sending || !newMessage.trim() ? 0.5 : 1,
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <ChevronRight size={20} />
                  </button>
                </form>
              </div>
            </div>
          ) : (
            <div style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'white'
            }}>
              <div style={{
                textAlign: 'center',
                color: COLORS.ACCENT_GRAY
              }}>
                <Message size={48} color={COLORS.ACCENT_GRAY} style={{ marginBottom: '16px' }} />
                <p>Select a conversation to start messaging</p>
              </div>
            </div>
          )
        ) : null}
      </div>

      {/* Bottom Navigation */}
      <BottomNavigation />
    </div>
  );
}
